import { db } from "./db";
import type { GnomuxFile, GnomuxObject } from "./objects";

const sequenceTables = [
  db.sequences,
  db.sequenceChunks,
  db.features,
  db.primers,
  db.sequenceCheckpoints,
  db.sequenceOperations,
  db.chromatograms,
  db.trackConfigs,
  db.blastSearches,
  db.blastResults,
  db.interproSearches,
  db.interproResults,
];

const objectDeletionTables = [
  ...sequenceTables,
  db.objects,
  db.recents,
  db.alignments,
  db.alignedSequences,
  db.collectionOperations,
  db.treeContent,
];

async function isSequenceReferenced(
  sequenceId: string,
  excludedFileIds: ReadonlySet<string>,
): Promise<boolean> {
  return db.objects
    .filter((object) => {
      if (excludedFileIds.has(object.id) || !("type" in object)) return false;

      if (object.type === "GnomuxSequence") {
        return object.content === sequenceId;
      }

      return (
        object.type === "GnomuxSequenceCollection" &&
        Array.isArray(object.content) &&
        object.content.includes(sequenceId)
      );
    })
    .count()
    .then((count) => count > 0);
}

export async function deleteSequence(sequenceId: string): Promise<void> {
  await db.transaction("rw", sequenceTables, async () => {
    await db.sequenceChunks
      .where("[sequenceId+index]")
      .between([sequenceId, -Infinity], [sequenceId, Infinity])
      .delete();
    await db.features.where("sequenceId").equals(sequenceId).delete();
    await db.primers.where("sequenceId").equals(sequenceId).delete();
    await db.sequenceCheckpoints
      .where("sequenceId")
      .equals(sequenceId)
      .delete();
    await db.sequenceOperations.where("sequenceId").equals(sequenceId).delete();
    await db.chromatograms.delete(sequenceId);
    await db.trackConfigs.where("sequenceId").equals(sequenceId).delete();

    const blastSearchIds = await db.blastSearches
      .where("sequenceId")
      .equals(sequenceId)
      .primaryKeys();

    for (const searchId of blastSearchIds) {
      await db.blastResults.where("searchId").equals(searchId).delete();
    }

    await db.blastSearches.bulkDelete(blastSearchIds);

    const interproSearchIds = await db.interproSearches
      .where("sequenceId")
      .equals(sequenceId)
      .primaryKeys();

    for (const searchId of interproSearchIds) {
      await db.interproResults.where("searchId").equals(searchId).delete();
    }

    await db.interproSearches.bulkDelete(interproSearchIds);

    await db.sequences.delete(sequenceId);
  });
}

/** Delete sequence data only when no file, including a trashed file, refers to it. */
export async function deleteSequenceIfUnreferenced(
  sequenceId: string,
  excludedFileIds: readonly string[] = [],
): Promise<boolean> {
  return db.transaction("rw", [...sequenceTables, db.objects], async () => {
    if (await isSequenceReferenced(sequenceId, new Set(excludedFileIds))) {
      return false;
    }

    await deleteSequence(sequenceId);
    return true;
  });
}

export async function deleteCollection(
  fileId: string,
  sequenceIds: string[],
): Promise<void> {
  await db.transaction(
    "rw",
    [...sequenceTables, db.collectionOperations, db.recents, db.objects],
    async () => {
      for (const sequenceId of sequenceIds) {
        await deleteSequenceIfUnreferenced(sequenceId, [fileId]);
      }

      await db.collectionOperations
        .where("collectionFileId")
        .equals(fileId)
        .delete();
      await db.recents.delete(fileId);
      await db.objects.delete(fileId);
    },
  );
}

export async function deleteAlignment(
  fileId: string,
  alignmentId: string,
): Promise<void> {
  await db.transaction(
    "rw",
    [db.alignedSequences, db.alignments, db.recents, db.objects],
    async () => {
      await db.alignedSequences
        .where("alignmentId")
        .equals(alignmentId)
        .delete();
      await db.alignments.delete(alignmentId);
      await db.recents.delete(fileId);
      await db.objects.delete(fileId);
    },
  );
}

export async function deleteDirectory(objectId: string): Promise<void> {
  await deleteObjectTree(objectId);
}

/**
 * Permanently delete an object and all descendants, including their backing
 * content. Sequence data is retained when a file outside the deleted subtree
 * still references it.
 */
export async function deleteObjectTree(objectId: string): Promise<void> {
  await db.transaction("rw", objectDeletionTables, async () => {
    const root = await db.objects.get(objectId);
    if (!root) return;

    const objects: GnomuxObject[] = [root];
    const visitedIds = new Set([root.id]);
    for (let index = 0; index < objects.length; index++) {
      const children = await db.objects
        .where("parent")
        .equals(objects[index].id)
        .toArray();
      for (const child of children) {
        if (visitedIds.has(child.id)) continue;
        visitedIds.add(child.id);
        objects.push(child);
      }
    }

    const objectIds = objects.map((object) => object.id);
    const excludedFileIds = new Set(objectIds);
    const sequenceIds = new Set<string>();
    const treeFileIds = new Set<string>();

    for (const object of objects) {
      if (!("type" in object)) continue;

      const file = object as GnomuxFile;
      if (file.type === "GnomuxTree") {
        treeFileIds.add(file.id);
        if ("content" in file && typeof file.content === "string") {
          await db.treeContent.delete(file.content);
        }
        continue;
      }

      if (!("content" in file)) continue;

      if (file.type === "GnomuxSequence" && typeof file.content === "string") {
        sequenceIds.add(file.content);
      } else if (
        file.type === "GnomuxSequenceCollection" &&
        Array.isArray(file.content)
      ) {
        for (const sequenceId of file.content) {
          sequenceIds.add(sequenceId);
        }
        await db.collectionOperations
          .where("collectionFileId")
          .equals(file.id)
          .delete();
      } else if (
        file.type === "GnomuxAlignment" &&
        typeof file.content === "string"
      ) {
        const alignmentId = file.content;
        await db.alignedSequences
          .where("alignmentId")
          .equals(alignmentId)
          .delete();
        await db.alignments.delete(alignmentId);
      }
    }

    if (treeFileIds.size > 0) {
      await db.alignments
        .filter((alignment) =>
          treeFileIds.has(alignment.sourceTreeFileId ?? ""),
        )
        .modify({ sourceTreeFileId: undefined });
    }

    for (const sequenceId of sequenceIds) {
      if (!(await isSequenceReferenced(sequenceId, excludedFileIds))) {
        await deleteSequence(sequenceId);
      }
    }

    await db.recents.bulkDelete(objectIds);
    await db.objects.bulkDelete(objectIds);
  });
}
